import * as React from 'react';
import {
    ICalloutPositionedInfo,
    IPosition,
} from 'office-ui-fabric-react/lib/utilities/positioning';

export function useRepositionableCallout() {
    const mouseDownX = React.useRef<number>(0);
    const mouseDownY = React.useRef<number>(0);
    const [position, setPosition] = React.useState<IPosition>();
    const [manuallyRepositioned, setManuallyRepositioned] = React.useState<boolean>(false);
    const onPositioned = React.useCallback(
        (positions: ICalloutPositionedInfo | undefined) => {
            setPosition({ ...positions?.elementPosition });
        },
        [position]
    );

    const calloutStyles = React.useMemo(() => {
        return position
            ? {
                  root: {
                      left: `${position.left}px!important`,
                      top: `${position.top}px!important`,
                      right: `${position.right}px!important`,
                      bottom: `${position.bottom}px!important`,
                  },
              }
            : undefined;
    }, [position]);

    const onMouseDown = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
            mouseDownX.current = e.pageX;
            mouseDownY.current = e.pageY;
            document.addEventListener('mousemove', onMouseMove, true);
            document.addEventListener('mouseup', onMouseUp, true);
        },
        [position]
    );

    const onMouseMove = (e: MouseEvent) => {
        const deltaX = e.pageX - mouseDownX.current;
        const deltaY = e.pageY - mouseDownY.current;

        if (!position) {
            return;
        }

        let newPosition = { ...position };

        if (position.left) {
            newPosition.left = (newPosition?.left || 0) + deltaX;
        } else {
            newPosition.right = (newPosition.right || 0) - deltaX;
        }

        if (position.top) {
            newPosition.top = (newPosition.top || 0) + deltaY;
        } else {
            newPosition.bottom = (newPosition.bottom || 0) - deltaY;
        }

        setPosition(newPosition);
        if (deltaX !== 0 || deltaY !== 0) {
            setManuallyRepositioned(true);
        }
    };

    const onMouseUp = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        document.removeEventListener('mousemove', onMouseMove, true);
        document.removeEventListener('mouseup', onMouseUp, true);
    };

    React.useEffect(() => {
        return () => {
            // in case component unmounts between mouseDown and mouseUp, clean up listeners
            document.removeEventListener('mousemove', onMouseMove, true);
            document.removeEventListener('mouseup', onMouseUp, true);
        };
    }, []);

    return {
        onMouseDown,
        onPositioned,
        calloutStyles,
        manuallyRepositioned,
    };
}
